import { T, useGT } from 'gt-react';

import { Input } from '@/components/ui/input';
import type { LayoutDraft, MappingDraftError } from './layoutForm';

interface Props {
  draft: LayoutDraft;
  headerListId: string;
  lookupError: MappingDraftError | undefined;
  onChange: (changes: Partial<LayoutDraft>) => void;
}

/**
 * The layout's row filters on zero and on blank, and the optional second file (e.g. Stelpro's
 * Feedback) matched to each row on a key column.
 */
export function LayoutFileSettings({
  draft,
  headerListId,
  lookupError,
  onChange,
}: Props) {
  const gt = useGT();
  const ctx = { context: 'placeholder in the upload layout editor' };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs">
          <T context="label before the upload layout's skip-on-zero columns">
            Also skip a row when one of these is 0:
          </T>
        </span>
        <Input
          className="h-8 min-w-64 flex-1"
          list={headerListId}
          placeholder={gt('e.g. CORE COST NET', ctx)}
          value={draft.skipWhenZero ?? ''}
          onChange={(event) => onChange({ skipWhenZero: event.target.value })}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs">
          <T context="label before the upload layout's skip-on-blank columns">
            Also skip a row when one of these is blank:
          </T>
        </span>
        <Input
          className="h-8 min-w-64 flex-1"
          list={headerListId}
          placeholder={gt('e.g. Pricing Factor', ctx)}
          value={draft.skipWhenBlank ?? ''}
          onChange={(event) => onChange({ skipWhenBlank: event.target.value })}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs">
          <T context="label before the upload layout's second-file settings">
            Second file (optional):
          </T>
        </span>
        <Input
          className="h-8 w-40"
          placeholder={gt('Name, e.g. Feedback', ctx)}
          value={draft.lookupLabel ?? ''}
          onChange={(event) => onChange({ lookupLabel: event.target.value })}
        />
        <span className="text-muted-foreground text-xs">
          <T context="upload layout editor: joins the results column to the second file's column">
            matches results column
          </T>
        </span>
        <Input
          className="h-8 w-44"
          list={headerListId}
          placeholder={gt('e.g. LOAD NUM', ctx)}
          value={draft.lookupMatchColumn ?? ''}
          onChange={(event) =>
            onChange({ lookupMatchColumn: event.target.value })
          }
        />
        <span className="text-muted-foreground text-xs">
          <T context="upload layout editor: the second file's key column">
            to its column
          </T>
        </span>
        <Input
          className="h-8 w-36"
          placeholder={gt('e.g. Num', ctx)}
          value={draft.lookupKeyColumn ?? ''}
          onChange={(event) =>
            onChange({ lookupKeyColumn: event.target.value })
          }
        />
      </div>
      <p className="text-muted-foreground text-xs">
        <T context="how formulas read the second file's columns">
          Formulas read the second file’s columns as [Name: column], e.g.
          [Feedback: Margin Increase].
        </T>
      </p>
      {lookupError ? (
        <p className="text-destructive text-xs">
          <T context="upload layout editor error about the second-file settings">
            Fill the second file’s name and both columns, or leave all three
            blank. The name cannot contain : [ or ].
          </T>
        </p>
      ) : null}
    </div>
  );
}
